"use client";

import { useMemo, useRef, useState } from "react";
import Image from "next/image";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import EquipmentDialog, { EquipmentItem } from "../opremi-teretanu/EquipmentDialog";
import { EQUIPMENT, localizeEquipment } from "@/lib/equipment-data";

const CARD_WIDTH = 280;
const SCROLL_AMOUNT = CARD_WIDTH + 16; // card width + gap

export default function HomeEquipmentSection() {
  const t = useTranslations("HomeEquipment");
  const locale = useLocale() as "hr" | "en";
  const equipment = useMemo(() => EQUIPMENT.map((item) => localizeEquipment(item, locale)), [locale]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [selectedItem, setSelectedItem] = useState<EquipmentItem | null>(null);

  function scrollLeft() {
    scrollRef.current?.scrollBy({ left: -SCROLL_AMOUNT, behavior: "smooth" });
  }

  function scrollRight() {
    scrollRef.current?.scrollBy({ left: SCROLL_AMOUNT, behavior: "smooth" });
  }

  return (
    <>
    <section id="sprave" className="mx-auto max-w-6xl px-6 py-12 sm:px-8 lg:px-10">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-3xl font-bold tracking-tight text-[#18181b] sm:text-4xl">
          {t("heading")}
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={scrollLeft}
            aria-label={t("prevAria")}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-[#E4E4E7] bg-white text-[#18181b] transition-colors hover:bg-[#F9FAFB]"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={scrollRight}
            aria-label={t("nextAria")}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-[#E4E4E7] bg-white text-[#18181b] transition-colors hover:bg-[#F9FAFB]"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Carousel */}
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto pb-2"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {equipment.map((item) => (
          <button
            key={item.id}
            onClick={() => setSelectedItem(item)}
            className="group flex flex-shrink-0 flex-col overflow-hidden rounded-2xl border border-[#E4E4E7] bg-white text-left transition-shadow hover:shadow-md"
            style={{ width: CARD_WIDTH }}
          >
            {/* Image area — white background */}
            <div className="relative flex h-52 w-full items-center justify-center bg-white p-4">
              <Image
                src={item.image}
                alt={item.name}
                fill
                className="object-contain p-4 transition-transform duration-300 group-hover:scale-105"
              />
            </div>

            {/* Content area — light gray */}
            <div className="flex flex-1 flex-col gap-2 bg-[#F9FAFB] px-4 py-3">
              <p className="text-base font-bold text-[#18181b]">{item.name}</p>
              <div className="flex flex-wrap gap-1.5">
                {item.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full border border-[#E4E4E7] bg-white px-3 py-0.5 text-xs font-medium text-[#18181b]"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </button>
        ))}
      </div>
    </section>

    <EquipmentDialog item={selectedItem} onClose={() => setSelectedItem(null)} showCta={false} />
    </>
  );
}
